<template>
    <div :class="preCls + 'content-box'">
        <div class="flex" style="margin-bottom: 10px">
            <div class="flex-item grow">
                <select-tree
                    v-model="filter.floorId"
                    style="width: 220px"
                    placeholder="请选择楼层"
                    :list="parkList"
                    :selectable="selectable"
                    fullPathName
                    @input="fetch"
                />
            </div>
            <Button
                v-if="$can('update')"
                type="primary"
                :loading="loading || loadingSave"
                @click="onSaveClick"
            >
                修改并保存
            </Button>
        </div>
        <Table
            ref="mt"
            :editMode="editMode"
            :formula="formula"
            :columns="columns"
            :data="roomList"
            :loading="loading"
            :height="mtHeight"
        >
        </Table>
    </div>
</template>

<script>
import mList from '_v/common/m-list'
import { mapCache } from '@/plugins/vux-data-cache'
import { editCell, pipeCell } from '_c/grid/renderCell'
import { isEmpty } from '_u/helper'
import { listToTree, yuan, cent, pascalcase } from '_u/helper'
import Num from '_u/number'
import {
    houseListByFloorNum,
    assetsTreeList,
    updateBatchFee,
} from '@/api/assets'

export default {
    name: 'AssetsParkModify',
    mixins: [mList],
    data() {
        return {
            loading: false,
            loadingSave: false,
            mtHeight: 360,
            editMode: 'row',
            dict: {},
            parkList: [],
            roomList: [],
        }
    },
    computed: {
        ...mapCache({
            filter: 'modifyFilter',
        }),
        columns() {
            return [
                { type: 'selection', width: 60, align: 'center' },
                {
                    type: 'index',
                    minWidth: 80,
                    title: '序号',
                    align: 'center',
                },
                {
                    key: 'houseName',
                    minWidth: 140,
                    title: '房源名称',
                    align: 'center',
                },
                {
                    key: 'buildArea',
                    minWidth: 100,
                    title: '面积(m2)',
                    align: 'right',
                    render: editCell('CellInputNumber', {
                        props: {
                            render: this.coloredAmount,
                        },
                        attrs: {
                            // 禁用小数
                            allowDecimal: false,
                            // 禁用负数
                            allowNegative: false,
                            // 最小值
                            min: 0,
                        },
                    }),
                },
                {
                    key: 'unitPrice',
                    minWidth: 130,
                    title: '当前单价(元/m2)',
                    align: 'right',
                    render: pipeCell('currency'),
                },
                {
                    key: 'curDeposit',
                    minWidth: 120,
                    title: '当前押金',
                    align: 'right',
                    render: pipeCell('currency'),
                },
                {
                    key: 'deposit',
                    minWidth: 120,
                    title: '设置押金',
                    align: 'right',
                    render: editCell('CellInputNumber', {
                        props: {
                            render: this.coloredAmount,
                        },
                        attrs: {
                            // 禁用负数
                            allowNegative: false,
                            // 最小值
                            min: 0,
                        },
                    }),
                },
                {
                    key: 'curRent',
                    minWidth: 120,
                    title: '当前租金(元/月)',
                    align: 'right',
                    render: pipeCell('currency'),
                },
                {
                    key: 'rent',
                    minWidth: 120,
                    title: '设置租金(元/月)',
                    align: 'right',
                    render: editCell('CellInputNumber', {
                        props: {
                            render: this.coloredAmount,
                        },
                        attrs: {
                            // 禁用负数
                            allowNegative: false,
                            // 最小值
                            min: 0,
                        },
                    }),
                },
            ]
        },
    },
    created() {
        this.fetchAssets().then(() => this.fetch())
    },
    mounted() {
        this.afterContainerResize((el, detector) => {
            this.mtHeight =
                detector.windowHeight -
                this.$refs.mt.$el.getBoundingClientRect().top -
                20
        })
    },
    methods: {
        // 保存
        onSaveClick() {
            var selection = this.$refs.mt.getSelection()

            if (selection.length) {
                this.loadingSave = true
                var houseList = selection.map((v) => ({
                    id: v.id,
                    buildArea: v.buildArea,
                    unitPrice: cent(v.unitPrice),
                    rent: cent(v.rent),
                    deposit: cent(v.deposit),
                }))
                updateBatchFee(JSON.stringify(houseList))
                    .then(() =>
                        this.$Message.success({
                            content: '修改成功',
                        })
                    )
                    .then((res) => this.fetch())
                    .finally(() => (this.loadingSave = false))
            } else {
                this.$Message.warning({
                    content: `请至少选中一个房间`,
                })
            }
        },
        // 公式
        formula(model) {
            // 单价 = 租金 / 面积
            model.equation(
                (d) => d.key == 'unitPrice',
                (d) =>
                    d.row['buildArea'] == 0
                        ? 0
                        : Num(d.row['rent']).div(d.row['buildArea']).dp(2)
            )

            // 面积不能为空
            model.verification(
                (d) => d.key == 'buildArea',
                (d) => [
                    {
                        validator(rule, value, cb, source, options) {
                            isEmpty(d.row.buildArea)
                                ? cb(new Error('面积不能为空'))
                                : cb()
                        },
                    },
                ]
            )
            // 押金不能为空
            model.verification(
                (d) => d.key == 'deposit',
                (d) => [
                    {
                        validator(rule, value, cb, source, options) {
                            isEmpty(d.row['deposit'])
                                ? cb(new Error('押金不能为空'))
                                : cb()
                        },
                    },
                ]
            )
            // 租金不能为空
            model.verification(
                (d) => d.key == 'rent',
                (d) => [
                    {
                        validator(rule, value, cb, source, options) {
                            isEmpty(d.row['rent'])
                                ? cb(new Error('租金不能为空'))
                                : cb()
                        },
                    },
                ]
            )
        },
        // 查询
        fetch() {
            this.loading = true

            var floorId = this.filter.floorId
            return Promise.all([
                floorId ? houseListByFloorNum(floorId) : Promise.resolve(),
            ])
                .then((args) => {
                    var [room] = args

                    // 房间数据
                    if (room) {
                        room.data.list.forEach((v) => {
                            v.unitPrice = yuan(v.unitPrice)
                            v.deposit = yuan(v.deposit)
                            v.rent = yuan(v.rent)

                            v.curBuildArea = v.buildArea
                            v.curDeposit = v.deposit
                            v.curRent = v.rent
                        })
                        this.roomList = room.data.list
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        // 查询楼层数据
        fetchAssets() {
            return assetsTreeList({ pageNo: 1, pageSize: 9999, level: 3 }).then(
                (res) => {
                    listToTree(res.data.list, {
                        level: '_level',
                        pId: 'pid',
                        done: (tree, map, list) => {
                            list.forEach((v) => {
                                if (v.level == '3') {
                                    v.id = v.parent.id + '_' + v.id
                                }

                                this.parkList = list
                            })
                        },
                    })
                }
            )
        },
        // 只能选末级
        selectable({ data }) {
            return data.isEnd
        },
        coloredAmount(h, { row, column }) {
            var k1 = column.key,
                k2 = `cur${pascalcase(k1)}`

            return row[k1] != row[k2]
                ? h('span', { style: { color: '#6300BF' } }, row[k1])
                : h('span', null, row[k1])
        },
    },
}
</script>

<style lang="less"></style>
